import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { ActionTypes } from '../context/AppContext';
import { useNeo4j } from '../hooks/useNeo4j';

function AddUserScreen() {
  const { dispatch } = useApp();
  const { createUserWithTraits } = useNeo4j();
  const [formData, setFormData] = useState({
    name: '',
    specialty: '',
    finishStyle: 'Brief + checklist',
    stepSize: '30-60 min blocks',
    commStyle: 'Quick voice/text summary',
    motivation: 'Deadlines',
    interrupt: 'Medium'
  });
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus(
      <div className="flex items-center justify-center gap-2 text-yellow-400">
        <div className="spinner w-5 h-5"></div>
        Saving to Neo4j...
      </div>
    );

    const result = await createUserWithTraits(formData);
    
    if (result.success) {
      setStatus(
        <div className="text-green-400">
          Successfully created user: {result.name}
        </div>
      );
      // Reset form
      setFormData({
        name: '',
        specialty: '',
        finishStyle: 'Brief + checklist',
        stepSize: '30-60 min blocks',
        commStyle: 'Quick voice/text summary',
        motivation: 'Deadlines',
        interrupt: 'Medium'
      });
    } else {
      setStatus(
        <div className="text-red-400">Error: {result.error}</div>
      );
    }
    
    setIsSubmitting(false);
  };

  const goBackToDashboard = () => {
    dispatch({ type: ActionTypes.SET_SCREEN, payload: 'dashboard' });
  };

  return (
    <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-2xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-teal-400">Create New User Profile</h1>
        <button 
          onClick={goBackToDashboard}
          className="text-sm text-teal-400 hover:underline"
        >
          ← Back to Dashboard
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Basic Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block mb-2 text-sm font-medium">
              Full Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="bg-gray-700 border border-gray-600 rounded-lg w-full p-2.5"
              placeholder="e.g., Priya Singh"
              required
            />
          </div>
          
          <div>
            <label htmlFor="specialty" className="block mb-2 text-sm font-medium">
              Specialty
            </label>
            <input
              type="text"
              id="specialty"
              name="specialty"
              value={formData.specialty}
              onChange={handleInputChange}
              className="bg-gray-700 border border-gray-600 rounded-lg w-full p-2.5"
              placeholder="e.g., (VFX Artist)"
              required
            />
          </div>
        </div>
        
        {/* PWG Traits */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="finishStyle" className="block mb-2 text-sm font-medium">
              Finish-line Style
            </label>
            <select
              id="finishStyle"
              name="finishStyle"
              value={formData.finishStyle}
              onChange={handleInputChange}
              className="bg-gray-700 border border-gray-600 rounded-lg w-full p-2.5"
            >
              <option>Problem statement only</option>
              <option>Example + guardrails</option>
              <option>Brief + checklist</option>
              <option>Tight spec</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="stepSize" className="block mb-2 text-sm font-medium">
              Typical Step Size
            </label>
            <select
              id="stepSize"
              name="stepSize"
              value={formData.stepSize}
              onChange={handleInputChange}
              className="bg-gray-700 border border-gray-600 rounded-lg w-full p-2.5"
            >
              <option>2-3 hr deep blocks</option>
              <option>30-60 min blocks</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="commStyle" className="block mb-2 text-sm font-medium">
              Default Relay Style
            </label>
            <select
              id="commStyle"
              name="commStyle"
              value={formData.commStyle}
              onChange={handleInputChange}
              className="bg-gray-700 border border-gray-600 rounded-lg w-full p-2.5"
            >
              <option>Artifact-first</option>
              <option>Brief + checklist</option>
              <option>Quick voice/text summary</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="motivation" className="block mb-2 text-sm font-medium">
              Motivation Trigger
            </label>
            <select
              id="motivation"
              name="motivation"
              value={formData.motivation}
              onChange={handleInputChange}
              className="bg-gray-700 border border-gray-600 rounded-lg w-full p-2.5"
            >
              <option>Autonomy</option>
              <option>Visible progress</option>
              <option>Fast feedback</option>
              <option>Deadlines</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="interrupt" className="block mb-2 text-sm font-medium">
              Interrupt Tolerance
            </label>
            <select
              id="interrupt"
              name="interrupt"
              value={formData.interrupt}
              onChange={handleInputChange}
              className="bg-gray-700 border border-gray-600 rounded-lg w-full p-2.5"
            >
              <option>Low</option>
              <option>Medium</option>
              <option>High</option>
            </select>
          </div>
        </div>
        
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full text-white bg-teal-600 hover:bg-teal-700 font-medium rounded-lg text-sm px-5 py-2.5 text-center flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : 'Save User to Neo4j'}
        </button>
      </form>
      
      {status && (
        <div className="mt-4 text-center">
          {status}
        </div>
      )}
    </div>
  );
}

export default AddUserScreen;
